/**
 * YourDay.tsx · 09_APP · OS-006 · capabilities C35, C36, C37, C38, C39, C40 · migrations v48, v54, v55, v56, v57
 *
 * Hours, water, how she is feeling. Hers, 2026-10-15. Two records joined by date (2026-08-11 ruling):
 * work_session for hours, wellness for the rest. Never guesses a time.
 *
 * Rebuilt 2026-10-19 to her rulings after using the first version (D-0061): "this needs to be super clear."
 *   Hours   · "It doesn't need to show me what I say, just log what I say. And then maybe a little thing
 *             you can click to like open up and see what you've listed for the day and if you need to
 *             edit it." Talk or type, Enter, it logs and says Saved. "See today's log" to check or fix.
 *   Water   · "when you click the cup, then it gives you the option. Was this an 8 ounce, 32 ounce,
 *             16 ounce? Simple." Tap a cup, pick the size, done.
 *   Feeling · "It should be like a journal." Tap a mood, or talk or type and Save; the box clears for
 *             the next entry. Every entry is kept, timed, for the patterns she asked for (C39 part 2).
 *   Mic     · "it kind of cut me off." Listens until she taps it again.
 *   Nudge   · C37: a line while hours or water are missing, and a pop-up every two hours if she allows it.
 *   Forgot  · C40: a stretch left open from an earlier day asks "When did you stop?" first, with her last
 *             logged activity offered and hers to change. Never books the night as work.
 */

import { useEffect, useState } from "react";
import { os, OsError, type MyDay } from "../lib/os";
import { useDictation } from "../lib/speech";
import { parseHours } from "../lib/spoken_time";
import { dayName, defaultStopTime, stopAt } from "../lib/stale_close";

const MOODS: Array<[string, string]> = [
  ["great", "Great"], ["good", "Good"], ["okay", "Okay"],
  ["tired", "Tired"], ["stressed", "Stressed"], ["unwell", "Unwell"],
];
const GLASSES = 8;
const GOAL_OZ = GLASSES * 8;
const SIZES = [8, 16, 32];
const NUDGE_EVERY_MS = 2 * 60 * 60 * 1000;
const NUDGE_KEY = "c2c-yourday-last-nudge";

function t(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function nowT() {
  return new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function missingLine(day: MyDay | null): string | null {
  if (!day) return null;
  const noHours = day.sessions.length === 0;
  const noWater = !(day.water_oz || day.water_glasses);
  if (noHours && noWater) return "Emily, you haven't logged your hours or your water yet today. Please do, when you can.";
  if (noHours) return "Emily, you haven't logged your hours yet today. Tell me when you started.";
  if (noWater) return "Emily, you haven't logged any water yet today. Have a glass and tap a cup.";
  return null;
}

function Mic({ on, busy, onClick, label }: { on: boolean; busy?: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" className={on ? "micbtn on" : "micbtn"} disabled={busy} onClick={onClick}
      aria-label={on ? "Stop listening" : label} title={on ? "Tap to stop" : label}>
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path fill="currentColor" d="M12 15a3.5 3.5 0 0 0 3.5-3.5v-6a3.5 3.5 0 0 0-7 0v6A3.5 3.5 0 0 0 12 15Zm6-3.5a.9.9 0 1 0-1.8 0 4.2 4.2 0 0 1-8.4 0 .9.9 0 1 0-1.8 0 6 6 0 0 0 5.1 5.93V20H8.5a.9.9 0 1 0 0 1.8h7a.9.9 0 1 0 0-1.8h-2.6v-2.57A6 6 0 0 0 18 11.5Z"/>
      </svg>
    </button>
  );
}

export function YourDay() {
  const [day, setDay] = useState<MyDay | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  const [notifyOk, setNotifyOk] = useState(typeof Notification !== "undefined" && Notification.permission === "granted");

  const [said, setSaid] = useState("");
  const [hoursMsg, setHoursMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const [askSize, setAskSize] = useState(false);
  const [lastPour, setLastPour] = useState<number | null>(null);
  const [waterMsg, setWaterMsg] = useState<string | null>(null);

  const [note, setNote] = useState("");
  const [feelMsg, setFeelMsg] = useState<string | null>(null);
  const [showJournal, setShowJournal] = useState(false);

  const [stopTime, setStopTime] = useState("");
  const [staleMsg, setStaleMsg] = useState<string | null>(null);

  const hoursMic = useDictation();
  const feelMic = useDictation();

  async function run(p: Promise<MyDay>) {
    setBusy(true);
    try { const d = await p; setDay(d); setErr(null); return true; }
    catch (e) { setErr(e instanceof OsError ? e.message : String(e)); return false; }
    finally { setBusy(false); }
  }

  useEffect(() => { void run(os.myDay()); }, []);

  // C40: offer her last logged activity as the stop time, once per stale stretch.
  const staleId = day?.stale_open?.id ?? null;
  useEffect(() => { if (day?.stale_open) setStopTime(defaultStopTime(day.stale_open)); }, [staleId]);

  async function closeStale() {
    const s = day?.stale_open;
    if (!s) return;
    const end = stopAt(s.on_date, stopTime);
    if (!end) { setStaleMsg("Pick the time you stopped."); return; }
    if (await run(os.workCloseStale(s.id, end))) setStaleMsg(`Saved ✓ ${dayName(s.on_date)} closed at ${t(end.toISOString())}. Ready for today.`);
  }
  useEffect(() => { const id = setInterval(() => setTick((n) => n + 1), 60000); return () => clearInterval(id); }, []);

  // C37: every two hours while something is missing, a pop-up if she allowed it.
  useEffect(() => {
    function check() {
      const line = missingLine(day);
      if (!line || !notifyOk || typeof Notification === "undefined") return;
      let last = 0;
      try { last = Number(localStorage.getItem(NUDGE_KEY) || 0); } catch { /* private window */ }
      if (Date.now() - last < NUDGE_EVERY_MS) return;
      try { new Notification("Your day", { body: line, icon: "/brand/C2C_logo_LIGHT.png" }); } catch { /* blocked */ }
      try { localStorage.setItem(NUDGE_KEY, String(Date.now())); } catch { /* private window */ }
    }
    check();
    const id = setInterval(check, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [day, notifyOk]);

  // ---------- hours: log what she says, no echo ----------
  async function logHours(text: string) {
    const v = text.trim();
    if (!v) return;
    const p = parseHours(v);
    const running = day?.sessions.find((s) => !s.ended_at);
    let ok = false;
    let msg = "";
    if (p && p.action !== "what") {
      ok = await run(os.workSaid(p.action,
        p.action === "start" || p.action === "stretch" ? p.start : null,
        p.action === "stop" || p.action === "stretch" ? p.end : null,
        p.what));
      msg = p.summary;
    } else if (running) {
      ok = await run(os.workSaid("what", null, null, p?.what || v));
      msg = "Noted what you're working on";
    } else {
      setHoursMsg({ ok: false, text: 'Say when you started first, like "I started at 10".' });
      return;
    }
    if (ok) { setSaid(""); setHoursMsg({ ok: true, text: `Saved ✓ ${msg}` }); }
  }

  // ---------- water: tap a cup, pick the size ----------
  async function pour(oz: number) {
    setAskSize(false);
    if (await run(os.addWater(oz))) { setLastPour(oz); setWaterMsg(`Saved ✓ ${oz} oz`); }
  }
  async function undoPour() {
    if (lastPour && await run(os.addWater(-lastPour))) { setLastPour(null); setWaterMsg("Taken back ✓"); }
  }

  // ---------- feeling: a journal ----------
  async function journal(mood: string | null, text: string) {
    const v = text.trim();
    if (!mood && !v) return;
    if (await run(os.feel(mood, v))) {
      if (v) setNote("");
      setFeelMsg(`Saved ✓ ${nowT()}`);
    }
  }

  async function allowPopups() {
    if (typeof Notification === "undefined") return;
    const r = await Notification.requestPermission();
    setNotifyOk(r === "granted");
  }

  const sessions = day?.sessions ?? [];
  const entries = day?.entries ?? [];
  const open = sessions.find((s) => !s.ended_at);
  const first = sessions[0];
  const workedMs = sessions.reduce(
    (a, s) => a + ((s.ended_at ? new Date(s.ended_at).getTime() : Date.now()) - new Date(s.started_at).getTime()), 0);
  const hours = (workedMs / 3600000).toFixed(1);
  const oz = day?.water_oz ?? (day?.water_glasses ?? 0) * 8;
  const filled = Math.min(GLASSES, Math.round(oz / 8));
  const nudge = missingLine(day);
  const lastMood = [...entries].reverse().find((e) => e.mood)?.mood ?? day?.mood ?? null;

  const last = sessions[sessions.length - 1];
  let line = "Not started yet.";
  if (open) line = `Working since ${t(open.started_at)}` + (first && sessions.length > 1 ? `, first started ${t(first.started_at)}.` : ".");
  else if (first && last?.ended_at) line = `Started ${t(first.started_at)}, last stopped ${t(last.ended_at)}.`;

  return (
    <section className="me" aria-label="Your day">
      <div className="top">
        <h3>Your day</h3>
        <div className="sub">Hours for grants, the rest just for you. Kept as separate records.</div>
      </div>

      {nudge && (
        <div className="nudge" role="status">
          <span>{nudge}</span>
          {typeof Notification !== "undefined" && !notifyOk && Notification.permission !== "denied" && (
            <button className="linkbtn" onClick={allowPopups}>Remind me every 2 hours</button>
          )}
        </div>
      )}

      {day?.stale_open && (
        <div className="stalebox" role="alert">
          <div className="stale-h">Yesterday's work is still open. When did you stop?</div>
          <div className="small">
            {dayName(day.stale_open.on_date)}, started {t(day.stale_open.started_at)}
            {day.stale_open.what ? `, working on ${day.stale_open.what}` : ""}.
            {defaultStopTime(day.stale_open)
              ? " The time below is the last thing you logged that day. Change it if you stopped later."
              : " Nothing else was logged that day, so tell me the time."}
          </div>
          <div className="row">
            <input type="time" className="note-in stale-time" value={stopTime}
              onChange={(e) => { setStopTime(e.target.value); setStaleMsg(null); }}
              aria-label="The time you stopped" />
            <button className="btn go" disabled={busy || !stopTime} onClick={() => void closeStale()}>Save and start fresh</button>
          </div>
          {staleMsg && <div className="small warnline">{staleMsg}</div>}
        </div>
      )}
      {!day?.stale_open && staleMsg && <div className="small savedline" style={{ marginTop: 10 }}>{staleMsg}</div>}

      {err && <div className="refusebox" style={{ marginTop: 10 }}>Couldn't save that: {err}</div>}
      <div className="grid">

        {/* HOURS */}
        <div className="tile">
          <div className="lbl">Hours today</div>
          <div className="big">{day ? hours : "…"}</div>
          <div className="small">{line}</div>
          <div className="row">
            {!open && (
              <button className="btn navy" disabled={busy || !day || !!day?.stale_open} onClick={() => run(os.workStart())}>
                {sessions.length ? "Back to work" : "Start my day"}
              </button>
            )}
            {open && (
              <>
                <button className="btn ghost" disabled={busy} onClick={() => run(os.workStop())}>Take a break</button>
                <button className="btn go" disabled={busy} onClick={() => run(os.workStop())}>Done for today</button>
              </>
            )}
          </div>
          <div className="sayrow">
            <input
              className="note-in"
              placeholder="Talk or type: started at 10, working on…"
              value={hoursMic.listening ? hoursMic.live : said}
              onChange={(e) => { setSaid(e.target.value); setHoursMsg(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") void logHours(said); }}
              aria-label="Tell me your hours and what you are working on"
            />
            {hoursMic.supported && (
              <Mic on={hoursMic.listening} busy={busy} label="Talk your hours"
                onClick={() => { setHoursMsg(null); hoursMic.toggle((text) => void logHours(text)); }} />
            )}
            <button className="btn navy enterbtn" disabled={busy || hoursMic.listening || !said.trim()} onClick={() => void logHours(said)}>Enter</button>
          </div>
          {hoursMic.listening && <div className="small listening">Listening. Tap the mic when you're done.</div>}
          {hoursMic.error && <div className="small warnline">{hoursMic.error}</div>}
          {hoursMsg && <div className={hoursMsg.ok ? "small savedline" : "small warnline"}>{hoursMsg.text}</div>}

          {sessions.length > 0 && (
            <button className="linkbtn logtoggle" onClick={() => setShowLog(!showLog)}>
              {showLog ? "Hide today's log" : `See today's log (${sessions.length})`}
            </button>
          )}
          {showLog && (
            <ul className="daylog">
              {sessions.map((s) => (
                <li key={s.id}>
                  <div className="when">{t(s.started_at)}{s.ended_at ? ` to ${t(s.ended_at)}` : " to now"}</div>
                  <input className="note-in" value={edits[s.id] ?? s.what ?? ""} placeholder="What you worked on"
                    onChange={(e) => setEdits({ ...edits, [s.id]: e.target.value })} aria-label="What you worked on" />
                  <div className="row">
                    {edits[s.id] !== undefined && edits[s.id] !== (s.what ?? "") && (
                      <button className="btn go" disabled={busy}
                        onClick={async () => { if (await run(os.workEdit(s.id, edits[s.id]!))) { const n = { ...edits }; delete n[s.id]; setEdits(n); } }}>Save</button>
                    )}
                    <button className="btn plain" disabled={busy}
                      onClick={() => { if (window.confirm("Remove this stretch from today's hours?")) void run(os.workRemove(s.id)); }}>Remove</button>
                  </div>
                </li>
              ))}
              <li className="small">Wrong time? Just say it again, like "I started at 9:45".</li>
            </ul>
          )}
        </div>

        {/* WATER */}
        <div className="tile">
          <div className="lbl">Water</div>
          <div className="big">{oz} <span className="small" style={{ fontSize: 14 }}>of {GOAL_OZ} oz</span></div>
          <div className="small">Tap a cup each time you drink.</div>
          <div className="glasses">
            {Array.from({ length: GLASSES }, (_, i) => (
              <button key={i} className={i < filled ? "glass on" : "glass"} aria-label="Log a drink"
                disabled={busy || !day} onClick={() => { setWaterMsg(null); setAskSize(true); }} />
            ))}
          </div>
          {askSize && (
            <div className="sizes" role="group" aria-label="How much was it?">
              <div className="small">How much was it?</div>
              <div className="row">
                {SIZES.map((n) => (
                  <button key={n} className="btn navy" disabled={busy} onClick={() => void pour(n)}>{n} oz</button>
                ))}
                <button className="btn plain" onClick={() => setAskSize(false)}>Cancel</button>
              </div>
            </div>
          )}
          {waterMsg && (
            <div className="small savedline">
              {waterMsg}
              {lastPour !== null && <> · <button className="linkbtn" disabled={busy} onClick={() => void undoPour()}>Undo</button></>}
            </div>
          )}
        </div>

        {/* FEELING: a journal */}
        <div className="tile">
          <div className="lbl">How you're feeling</div>
          <div className="moods">
            {MOODS.map(([k, label]) => (
              <button key={k} className={lastMood === k ? "mood on" : "mood"} disabled={busy || !day}
                onClick={() => void journal(k, "")}>{label}</button>
            ))}
          </div>
          <div className="sayrow">
            <textarea
              className="note-in journal-in"
              rows={2}
              placeholder="Talk or type what's going on."
              value={feelMic.listening ? feelMic.live : note}
              onChange={(e) => { setNote(e.target.value); setFeelMsg(null); }}
              aria-label="How you are feeling, in your words"
            />
            {feelMic.supported && (
              <Mic on={feelMic.listening} busy={busy || !day} label="Talk how you're feeling"
                onClick={() => { setFeelMsg(null); feelMic.toggle((text) => setNote((n) => (n.trim() ? `${n.trim()} ${text}` : text))); }} />
            )}
          </div>
          <div className="row">
            <button className="btn navy" disabled={busy || feelMic.listening || !note.trim()} onClick={() => void journal(null, note)}>Save</button>
          </div>
          {feelMic.listening && <div className="small listening">Listening. Tap the mic when you're done.</div>}
          {feelMic.error && <div className="small warnline">{feelMic.error}</div>}
          {feelMsg && <div className="small savedline">{feelMsg}</div>}

          {entries.length > 0 && (
            <button className="linkbtn logtoggle" onClick={() => setShowJournal(!showJournal)}>
              {showJournal ? "Hide today's journal" : `Today's journal (${entries.length})`}
            </button>
          )}
          {showJournal && (
            <ul className="daylog">
              {entries.map((e) => (
                <li key={e.id}>
                  <div className="when">{t(e.at)}{e.mood ? ` · ${MOODS.find(([k]) => k === e.mood)?.[1] ?? e.mood}` : ""}</div>
                  {e.note && <div className="jnote">{e.note}</div>}
                  <button className="btn plain" disabled={busy}
                    onClick={() => { if (window.confirm("Remove this journal entry?")) void run(os.feelRemove(e.id)); }}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
